// Top navigation bar for desktop
import { motion } from 'framer-motion';
import { Translate } from 'i18n';
import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import ProfileMenu from 'students/views/pages/Member/components/navigation/components/ProfileMenu';
import { useTypedDispatch, useTypedSelector } from 'students/stores/_utils';
import { articlesActions } from 'students/stores/articles/slice';
import { selectArticlesUnreadCount } from 'students/stores/articles/selectors';
import WhatsNewPanel from 'students/views/pages/Member/components/whatsNew/WhatsNewPanel';
import ProgressTeaser from 'students/views/pages/Member/components/home/components/ProgressTeaser';
import LinkElem from 'students/views/shared/components/LinkElem';
import { NewBadge } from 'students/views/shared/components/NewBadge';
import { HeaderSupportDropdown } from 'students/views/shared/components/languagesSelection/LangDropdown';
import { useBreakPoint } from 'students/views/shared/hooks';
import { useFixedSurface } from 'students/views/shared/providers/Theme';
import { fixedSurfaceInk, onLightTheme } from 'students/views/shared/styled';
import userVariables from 'students/views/shared/styled/userVariables';
import styled from 'styled-components';
import { navigationBreakPoint, navigationDesktopBreakPoint } from '../theme/breakpoints';
import GoToTeacherArea from './GoToTeacherArea';
import ChatIcon from './components/ChatIcon';
import { INavigationItem, navigationMediaQuery } from './config';
import useNavBarItems from './hooks/useNavBarItems';
import {
  IconLink,
  NAV_ROW_CLASS_NAME,
  NAV_ROW_TEXT_CLASS_NAME,
  SRoteLink,
  desktopBadgeStyles
} from './styles';
import useNavBar from './useNavBar';
import MobileChatIcon from './components/MobileChatIcon';

type tProps = {
  mobileVariant?: 'small' | 'none';
};
const SUPPORT_LANG_DROPDOWN_CONTENT_CLASS_NAME = 'memberSupportDropdownContent';
const HOME_PATH = '/';
const NavBar: FC<tProps> = ({ mobileVariant }) => {
  const { logoSrc, userProfile, handleLogOut, isNavigationVisible } = useNavBar();
  const isFixedSurface = useFixedSurface();
  const navBarItems = useNavBarItems();
  const isDesktop = useBreakPoint('xl');
  const location = useLocation();
  const dispatch = useTypedDispatch();
  const unreadCount = useTypedSelector(selectArticlesUnreadCount);

  if (!isNavigationVisible && mobileVariant === 'none') {
    return null;
  }

  const checkNavLinkIsActive =
    (item: INavigationItem) =>
    (match: Record<string, any> | null, location: Record<string, any>): boolean => {
      if (!match) {
        return false;
      }

      if (match.isExact && match.path.slice(1) === item.path) {
        return true;
      }

      if (location?.state?.isSubRoute && match.path.slice(1) === item.path) {
        return true;
      }

      return match.isExact;
    };

  const renderNavBarItem = (item: INavigationItem) => {
    const link = item.isDynamic ? (
      <motion.div
        key={`${item.name}_motion`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <SRoteLink
          isActive={checkNavLinkIsActive(item)}
          activeClassName="active"
          to={item.path}
        >
          {item.icon && <IconLink $src={item.icon} />}
          <Translate scope="frontend.mobile_tabs" str={item.caption ?? item.name} />
        </SRoteLink>
      </motion.div>
    ) : (
      <SRoteLink
        isActive={checkNavLinkIsActive(item)}
        activeClassName="active"
        key={`${item.name}_link`}
        to={item.path}
      >
        {item.icon && <IconLink $src={item.icon} />}
        <Translate scope="frontend.mobile_tabs" str={item.caption ?? item.name} />
      </SRoteLink>
    );

    return item.badgeCount ? (
      <SBadgeWrapper
        key={`${item.name}_badge`}
        $badgeCount={item.badgeCount}
        $isActive={item.path === location.pathname}
      >
        {link}
      </SBadgeWrapper>
    ) : (
      link
    );
  };

  return (
    <>
      <SNavBarWrapper $isFixedSurface={isFixedSurface}>
        <SNavBar>
          {isNavigationVisible ? (
            <SNavigation>
              <SLogoLink to="/">
                <SLogo src={logoSrc} alt="Lingu" />
              </SLogoLink>

              <SItems>
                {navBarItems.map(renderNavBarItem)}

                <ChatIcon />
              </SItems>
            </SNavigation>
          ) : null}

          {/* Small screens have no room for the home header's stats card, so the
              bar form takes over the empty start of this row instead. */}
          {!isNavigationVisible && userProfile && location.pathname === HOME_PATH ? (
            <SNavBarProgressTeaser variant="bar" />
          ) : null}

          <SProfileContainer>
            {isDesktop && (
              <>
                <SAccountGroup>
                  <button
                    type="button"
                    className={`${NAV_ROW_CLASS_NAME} ln-gap-[8px] ln-rounded-[14px] ln-w-full ln-bg-transparent ln-border-0 ln-cursor-pointer ln-duration-150`}
                    onClick={() => dispatch(articlesActions.openPanel())}
                  >
                    <span
                      className={`${NAV_ROW_TEXT_CLASS_NAME} ln-flex-1 ln-font-normal ln-whitespace-nowrap ln-text-start`}
                    >
                      <Translate
                        str="frontend.whats_new.title"
                        params={{ defaultValue: "What's new?" }}
                      />
                    </span>
                    {unreadCount > 0 && <NewBadge />}
                  </button>
                  {isNavigationVisible && userProfile?.isTeacher ? (
                    <GoToTeacherArea />
                  ) : null}
                </SAccountGroup>
                <SDivider />
              </>
            )}

            {!isDesktop && <MobileChatIcon />}
            <SAccountGroup>
              <SHeaderSupportDropdown
                direction="up"
                contentClassName={SUPPORT_LANG_DROPDOWN_CONTENT_CLASS_NAME}
                withIndicateError
              />

              <ProfileMenu userProfile={userProfile} handleLogOut={handleLogOut} />
            </SAccountGroup>
          </SProfileContainer>
        </SNavBar>
      </SNavBarWrapper>
      <WhatsNewPanel />
    </>
  );
};

export default NavBar;

const SNavBarWrapper = styled.div<{ $isFixedSurface: boolean }>`
  width: 100%;
  padding-top: env(safe-area-inset-top);
  z-index: 21;

  /* The phone bar is transparent and covers the pages' top-left back button, so only its controls take taps. */
  @media (max-width: ${navigationBreakPoint - 1}px) {
    pointer-events: none;
  }

  ${({ $isFixedSurface }) =>
    $isFixedSurface &&
    onLightTheme`
      @media (max-width: ${navigationDesktopBreakPoint - 1}px) {
        ${fixedSurfaceInk}
        --navigation-navbar-background-color: rgba(0, 0, 0, 0.3);
      }
    `}

  ${navigationMediaQuery} {
    height: 80px;
  }

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    height: auto;
    width: auto;
    position: sticky;
  }
  display: flex;
`;

const SNavBar = styled.header`
  padding: 1rem;
  width: 100%;
  justify-content: flex-end;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 44px;

  @media (max-width: ${navigationBreakPoint - 1}px) {
    > * {
      pointer-events: auto;
    }
  }

  a {
    text-decoration: none;
  }

  ${navigationMediaQuery} {
    height: 80px;
    padding: 0 2rem;
    background: var(--navigation-navbar-background-color);
    justify-content: space-between;
  }

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    position: sticky;
    top: 0;
    flex-direction: column;
    align-items: stretch;
    flex-shrink: 0;
    max-height: 100vh;
    height: initial;
    width: ${userVariables.sidebarNavigationWidth};
    padding: 32px 15px 24px 16px;
    background: var(--navigation-navbar-background-color);
    border-inline-end: 1px solid rgb(var(--ln-overlay) / 0.1);
    justify-content: space-between;

    /* Scrolling lives on SNavigation, not here: an overflow other than 'visible'
       on one axis forces the other axis to clip too, so the sidebar used to cut
       off the language dropdown that opens above the profile section. */
    overflow: visible;
  }
`;

/* Claims the start of the bar and yields every pixel the icon cluster needs:
   it is the only item here allowed to shrink. */
const SNavBarProgressTeaser = styled(ProgressTeaser)`
  margin-inline-end: auto;
  min-width: 0;
  flex: 0 1 auto;
`;

const SProfileContainer = styled.nav`
  display: flex;
  align-items: center;
  justify-self: end;
  gap: 8px;
  flex-shrink: 0;

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    margin-block-start: auto;
  }
`;

/* Passes through in the compact bar so only the desktop drawer gets the grouping. */
const SAccountGroup = styled.div`
  display: contents;

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
  }
`;

const SDivider = styled.div`
  display: none;

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    display: block;
    width: 100%;
    height: 1px;
    background: rgb(var(--ln-overlay) / 0.1);
  }
`;

const SItems = styled.div`
  display: contents;

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
`;

const SNavigation = styled.nav`
  ${navigationMediaQuery} {
    display: flex;
    align-items: center;
  }

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 24px;
    /* Takes over the sidebar's scrolling so the links still collapse into a
       scroll area when they outgrow the viewport. */
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }
`;

const SHeaderSupportDropdown = styled(HeaderSupportDropdown)`
  & .${SUPPORT_LANG_DROPDOWN_CONTENT_CLASS_NAME} {
    inset-inline-start: auto;
    inset-inline-end: -50px;
    transform: translateX(0) !important;

    @media (min-width: ${navigationDesktopBreakPoint}px) {
      inset-inline-start: 0 !important;
      transform: translateX(0) !important;
      top: auto !important;
      bottom: calc(100% + 4px) !important;
    }
  }

  @media screen and (min-width: 1200px) {
    & > button {
      border: none !important;
      border-radius: 16px !important;

      & > .button-inner {
        gap: 20px;
        padding: 0 17.543px !important;
        color: rgb(var(--ln-content)) !important;
        font-weight: 400 !important;
        letter-spacing: -0.32px;

        & > span {
          flex: 1;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 16px !important;
          line-height: normal;
        }
      }
    }
  }
`;

const SBadgeWrapper = styled.div<{ $badgeCount?: number; $isActive: boolean }>`
  position: relative;

  ${({ $badgeCount, $isActive }) =>
    $badgeCount
      ? `
      a, button {
        padding-right: 38px;
      }

      &:after {
        content: '${$badgeCount}';
        display: flex;
        justify-content: center;
        align-items: center;
        width: 26px;
        height: 26px;
        background: ${$isActive ? 'rgb(11, 20, 32)' : 'rgb(var(--ln-badge-bg))'};
        border-radius: 8px;
        color: ${$isActive ? 'rgb(var(--ln-badge-active-ink))' : 'rgb(11, 20, 32)'};
        font-weight: 600;
        font-size: 15px;
        position: absolute;
        top: calc(50% - 13px);
        inset-inline-end: 10px;

        @media (min-width: ${navigationDesktopBreakPoint}px) {
          ${desktopBadgeStyles}
          background: ${$isActive ? '#000' : 'rgb(var(--ln-badge-bg))'};
          color: ${$isActive ? 'rgb(var(--ln-badge-active-ink))' : '#000'};
        }
      }
    `
      : ''}
`;

const SLogoLink = styled(LinkElem)`
  display: flex;
  flex-shrink: 0;

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    padding: 0 16px;
  }

  &:focus {
    outline: 3px solid var(--color-blue-btn-focus-outline-color);
    outline-offset: 5px;
    border-radius: 15px;
  }
`;

const SLogo = styled.img`
  width: 4rem;
  height: auto;
  margin-inline-end: 0.5rem;

  @media (min-width: ${navigationDesktopBreakPoint}px) {
    margin-inline-end: 0;
    width: 71px;
    height: 29px;
    margin-block: -0.5px;
    object-fit: cover;
  }
`;
